<template>
    <div class="windload-calculator-page">
        <div class="flex">
            <div class="landing-page-introduction-box">
                <div class="title">
                    <div>
                        Wind Load Calculator
                    </div>
                  <div>
                    <button class="btn-primary" @click="exportAsPdf()">
                      Print
                    </button>
                  </div>
                </div>
                <div class="main">
                    <div class="content">
                      <div class="sub-title mb-6">
                        Determining the design wind pressure to BS 6375-1 2015 - Building Heights up to 15m
                      </div>
                      <div class="flex-xs">
                        <div class="flex-1 pr-3-xs">
                          <div class="form-group">
                            <label>Basic Wind Speed (m/s)</label>
                            <select class="form-control" v-model="basic_wind_speed">
                              <option :value="21">21</option>
                              <option :value="22">22</option>
                              <option :value="23">23</option>
                              <option :value="24">24</option>
                              <option :value="25">25</option>
                              <option :value="26">26</option>
                              <option :value="27">27</option>
                              <option :value="28">28</option>
                              <option :value="29">29</option>
                              <option :value="30">30</option>
                              <option :value="31">31</option>
                            </select>
                            <p>
                              <em>
                                Select the Basic Wind Speed based on the Basic Wind Speed Map
                              </em>
                            </p>
                          </div>
                          <div class="form-group align-center align-left-xs">
                            <label>Basic Wind Speed Map</label>
                            <img class="ml-auto" src="/images/windload-calculator/basic-wind-speed-map.png" style="max-height:400px;">
                          </div>
                        </div>
                        <div class="flex-1 pl-3-xs">
                          <div class="form-group">
                            <label>Design Height</label>
                            <div class="input-group">
                              <input class="form-control" type="number" min="0" max="15" v-model="design_height">
                              <div class="input-group-addon">
                                m
                              </div>
                            </div>
                          </div>
                          <div class="form-group">
                            <label>Distance from sea</label>
                            <div class="input-group">
                              <input class="form-control" v-model="distance_from_sea">
                              <div class="input-group-addon">
                                Km
                              </div>
                            </div>
                          </div>
                          <div class="form-group">
                            <label>Location Type</label>
                            <select class="form-control" v-model="site_terrain_category_open_country_toggle">
                              <option :value="true">Open Country & up to 0.5km into town</option>
                              <option :value="false">More than 0.5km into town</option>
                            </select>
                            <p>
                              <em>
                                Select a Location Type to determine the Site Terrain Category and Wind load at sea level
                              </em>
                            </p>
                          </div>
                          <div class="flex">
                            <div class="flex-1 pr-3">
                              <div class="form-group">
                                <label>Site Terrain Category</label>
                                <div class="txt-lg">{{ site_terrain_category }}</div>
                              </div>
                            </div>
                            <div class="flex-1 pl-3">
                              <div class="form-group">
                                <label>Wind load at sea level</label>
                                <div class="txt-lg">{{ wind_load_at_sea_level.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }}</div>
                              </div>
                            </div>
                          </div>
                          <div class="flex-2xs">
                            <div class="flex-1 pr-3-2xs">
                              <div class="form-group">
                                <label>Altitude</label>
                                <div class="input-group">
                                  <input class="form-control" v-model="altitude">
                                  <div class="input-group-addon">
                                    m
                                  </div>
                                </div>
                                <p>
                                  <em>
                                    Enter the Altitude to determine the Altitude Factor
                                  </em>
                                </p>
                              </div>
                            </div>
                            <div class="flex-1 pl-3-2xs">
                              <div class="form-group">
                                <label>Altitude Factor</label>
                                <div class="txt-lg">{{ altitude_factor.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="flex-sm">
                        <div class="flex-1 pr-3-sm">
                          <div class="form-group">
                            <label>Dormer Factor</label>
                            <div class="radios _inline">
                              <div class="radio">
                                <label>
                                  <input type="radio" name="dormer_factor" :value="1" v-model="dormer_factor">
                                  <span class="radio-label">Do not apply dormer factor</span>
                                </label>
                              </div>
                              <div class="radio">
                                <label>
                                  <input type="radio" name="dormer_factor" :value="1.6" v-model="dormer_factor">
                                  <span class="radio-label">Apply dormer factor</span>
                                </label>
                              </div>
                            </div>
                            <p>
                              <em>
                                To allow for the fact that any form of vertical roof glazing, such as dormer windows, might be subject to higher loads than those on a vertical face, the wind load shall be multiplied by a dormer factor of 1.6 for windows in these locations. For all other situations a dormer factor of 1.0 shall be used.
                              </em>
                            </p>
                          </div>
                          <div class="form-group">
                            <label>Funnelling Factor</label>
                            <div class="radios _inline">
                              <div class="radio">
                                <label>
                                  <input type="radio" name="funnelling_factor" :value="1" v-model="funnelling_factor">
                                  <span class="radio-label">Do not apply funnelling factor</span>
                                </label>
                              </div>
                              <div class="radio">
                                <label>
                                  <input type="radio" name="funnelling_factor" :value="1.35" v-model="funnelling_factor">
                                  <span class="radio-label">Apply funnelling factor</span>
                                </label>
                              </div>
                            </div>
                            <p>
                              <em>
                                Where the two buildings under consideration are sheltered by upwind buildings that are of similar height (or higher) and spaced at not more than three building heights away then funnelling may be disregarded. Otherwise, where the walls of two buildings face each other and the gap between them is less than either the buildings width or twice the building height, then the design wind load for doorsets and windows in the facing walls shall be multiplied by a funnelling factor of 1.35.
                              </em>
                            </p>
                          </div>
                        </div>
                        <div class="flex-1 pl-3-sm">
                          <div class="form-group">
                            <label>Orographic Zones</label>
                            <img src="/images/windload-calculator/orographic-zones.png" style="max-height:350px;">
                          </div>
                          <div class="form-group">
                            <label>Orography Factor</label>
                            <select class="form-control" v-model="orography_factor">
                              <optgroup label="Zone 1">
                                <option :value="1">Category 1 - Nominally flat terrain, average slope < 1/20</option>
                                <option :value="1.25">Category 2 - Shallow terrain, average slope < 1/10</option>
                                <option :value="1.54">Category 3 - Moderately steep terrain, average slope < 1/10</option>
                                <option :value="1.85">Category 4 - Steep terrain, average slope < 1/5</option>
                              </optgroup>
                              <optgroup label="Zone 2">
                                <option :value="1">Category 1 - Nominally flat terrain, average slope < 1/20</option>
                                <option :value="1.14">Category 2 - Shallow terrain, average slope < 1/10</option>
                                <option :value="1.28">Category 3 - Moderately steep terrain, average slope < 1/10</option>
                                <option :value="1.44">Category 4 - Steep terrain, average slope < 1/5</option>
                              </optgroup>
                              <optgroup label="Zone 3">
                                <option :value="1">Category 1 - Nominally flat terrain, average slope < 1/20</option>
                                <option :value="1.1">Category 2 - Shallow terrain, average slope < 1/10</option>
                                <option :value="1.21">Category 3 - Moderately steep terrain, average slope < 1/10</option>
                                <option :value="1.32">Category 4 - Steep terrain, average slope < 1/5</option>
                              </optgroup>
                            </select>
                            <p>
                              <em>
                                Select a zone and category based on the Orographic Zones chart to determine the Orography Factor
                              </em>
                            </p>
                          </div>
                          <div class="form-group">
                            <label>Orography Factor</label>
                            <div class="txt-lg">{{ orography_factor.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }}</div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div class="sub-title mb-6">
                          Determining the strength required for structural members
                        </div>
                        <div class="flex-sm">
                          <div>
                            <table class="table">
                              <tr>
                                <td>
                                  Load Span
                                </td>
                                <td>
                                  l =
                                </td>
                                <td>
                                  <div class="input-group">
                                    <input class="form-control" type="number" v-model="load_span">
                                    <div class="input-group-addon">mm</div>
                                  </div>
                                </td>
                                <td>
                                </td>
                                <td>
                                </td>
                                <td>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                </td>
                                <td>
                                  A =
                                </td>
                                <td>
                                  <div class="input-group">
                                    <input class="form-control" type="number" v-model="load_span_a">
                                    <div class="input-group-addon">mm</div>
                                  </div>
                                </td>
                                <td>
                                  Load Width
                                </td>
                                <td>
                                  a =
                                </td>
                                <td>
                                  {{ load_width_a.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }}mm
                                </td>
                              </tr>
                              <tr>
                                <td>
                                </td>
                                <td>
                                  B =
                                </td>
                                <td>
                                  <div class="input-group">
                                    <input class="form-control" type="number" v-model="load_span_b">
                                    <div class="input-group-addon">mm</div>
                                  </div>
                                </td>
                                <td>
                                  Load Width
                                </td>
                                <td>
                                  b =
                                </td>
                                <td>
                                  {{ load_width_b.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }}mm
                                </td>
                              </tr>
                            </table>
                          </div>
                          <div class="align-center">
                            <img class="m-auto" src="/images/windload-calculator/wind-load-calculator-load-span-graphic.png" style="max-height:200px;">
                          </div>
                          <div>
                            <table class="table">
                              <tr>
                                <td>
                                  Design Wind Pressure
                                </td>
                                <td>
                                  w =
                                </td>
                                <td>
                                  {{ total_wind_load.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 0}) }} Pa
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  Deflection
                                </td>
                                <td>
                                  l/
                                </td>
                                <td>
                                  {{ deflection.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }}
                                </td>
                              </tr>
                              <tr>
                                <td>

                                </td>
                                <td>
                                  =
                                </td>
                                <td>
                                  {{ deflection_mm.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }} mm
                                </td>
                              </tr>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  <div class="highlight-bar">
                    <h1>Total Wind Load: {{ total_wind_load.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 0}) }} Pa</h1>
                    <h3>Windload at sea level: {{ wind_load_at_sea_level.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 0}) }} x Altitude factor: {{ altitude_factor.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }} x Orography factor: {{ orography_factor.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }} x Dormer factor: {{ dormer_factor.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }} x Funnelling factor: {{ funnelling_factor.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }}</h3>
                      <h1>EI Required = {{ ei_required.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 2}) }}  x 10<sup>9</sup> N.mm<sup>2</sup></h1>
                  </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: [
        'logged_in_user',
    ],
    data() {
        return {
          basic_wind_speed: 26,
          design_height: 2.5,
          distance_from_sea: 3,
          site_terrain_category_open_country_toggle: true,
          altitude: 35,
          orography_factor: 1.14,
          dormer_factor: 1,
          funnelling_factor: 1,
          wind_load_mappings: {
            A: {
              21: {
                1: 642,
                2: 752,
                3: 839,
                4: 907,
              },
              22: {
                1: 705,
                2: 826,
                3: 920,
                4: 995,
              },
              23: {
                1: 770,
                2: 902,
                3: 1006,
                4: 1088,
              },
              24: {
                1: 839,
                2: 983,
                3: 1095,
                4: 1185,
              },
              25: {
                1: 910,
                2: 1066,
                3: 1188,
                4: 1285,
              },
              26: {
                1: 985,
                2: 1153,
                3: 1285,
                4: 1390,
              },
              27: {
                1: 1062,
                2: 1244,
                3: 1386,
                4: 1499,
              },
              28: {
                1: 1142,
                2: 1337,
                3: 1491,
                4: 1612,
              },
              29: {
                1: 1225,
                2: 1435,
                3: 1599,
                4: 1730,
              },
              30: {
                1: 1311,
                2: 1535,
                3: 1711,
                4: 1851,
              },
              31: {
                1: 1400,
                2: 1639,
                3: 1827,
                4: 1976,
              },
            },
            B: {
              21: {
                1: 568,
                2: 705,
                3: 815,
                4: 904,
              },
              22: {
                1: 623,
                2: 773,
                3: 894,
                4: 992,
              },
              23: {
                1: 681,
                2: 845,
                3: 977,
                4: 1084,
              },
              24: {
                1: 742,
                2: 921,
                3: 1064,
                4: 1181,
              },
              25: {
                1: 805,
                2: 999,
                3: 1155,
                4: 1281,
              },
              26: {
                1: 871,
                2: 1080,
                3: 1249,
                4: 1386,
              },
              27: {
                1: 939,
                2: 1165,
                3: 1347,
                4: 1494,
              },
              28: {
                1: 1010,
                2: 1253,
                3: 1449,
                4: 1607,
              },
              29: {
                1: 1083,
                2: 1344,
                3: 1554,
                4: 1724,
              },
              30: {
                1: 1159,
                2: 1438,
                3: 1663,
                4: 1845,
              },
              31: {
                1: 1238,
                2: 1536,
                3: 1776,
                4: 1970,
              },
            },
            C: {
              21: {
                1: 514,
                2: 639,
                3: 740,
                4: 824,
              },
              22: {
                1: 565,
                2: 702,
                3: 813,
                4: 904,
              },
              23: {
                1: 617,
                2: 767,
                3: 888,
                4: 988,
              },
              24: {
                1: 672,
                2: 835,
                3: 967,
                4: 1076,
              },
              25: {
                1: 729,
                2: 906,
                3: 1049,
                4: 1167,
              },
              26: {
                1: 789,
                2: 980,
                3: 1135,
                4: 1263,
              },
              27: {
                1: 850,
                2: 1057,
                3: 1224,
                4: 1362,
              },
              28: {
                1: 915,
                2: 1137,
                3: 1316,
                4: 1464,
              },
              29: {
                1: 981,
                2: 1219,
                3: 1412,
                4: 1571,
              },
              30: {
                1: 1050,
                2: 1305,
                3: 1511,
                4: 1681,
              },
              31: {
                1: 1121,
                2: 1393,
                3: 1614,
                4: 1795,
              },
            },
            D: {
              21: {
                1: 477,
                2: 666,
                3: 822,
                4: 907,
              },
              22: {
                1: 523,
                2: 731,
                3: 902,
                4: 995,
              },
              23: {
                1: 572,
                2: 799,
                3: 986,
                4: 1088,
              },
              24: {
                1: 623,
                2: 869,
                3: 1073,
                4: 1185,
              },
              25: {
                1: 676,
                2: 943,
                3: 1165,
                4: 1285,
              },
              26: {
                1: 731,
                2: 1020,
                3: 1260,
                4: 1390,
              },
              27: {
                1: 788,
                2: 1100,
                3: 1358,
                4: 1499,
              },
              28: {
                1: 848,
                2: 1183,
                3: 1461,
                4: 1612,
              },
              29: {
                1: 909,
                2: 1269,
                3: 1567,
                4: 1730,
              },
              30: {
                1: 973,
                2: 1359,
                3: 1677,
                4: 1851,
              },
              31: {
                1: 1039,
                2: 1451,
                3: 1791,
                4: 1976,
              },
            },
            E: {
              21: {
                1: 460,
                2: 641,
                3: 798,
                4: 904,
              },
              22: {
                1: 505,
                2: 704,
                3: 876,
                4: 992,
              },
              23: {
                1: 552,
                2: 769,
                3: 958,
                4: 1084,
              },
              24: {
                1: 601,
                2: 838,
                3: 1043,
                4: 1181,
              },
              25: {
                1: 652,
                2: 909,
                3: 1132,
                4: 1281,
              },
              26: {
                1: 705,
                2: 983,
                3: 1224,
                4: 1386,
              },
              27: {
                1: 760,
                2: 1060,
                3: 1320,
                4: 1494,
              },
              28: {
                1: 818,
                2: 1140,
                3: 1420,
                4: 1607,
              },
              29: {
                1: 877,
                2: 1223,
                3: 1523,
                4: 1724,
              },
              30: {
                1: 939,
                2: 1309,
                3: 1630,
                4: 1845,
              },
              31: {
                1: 1003,
                2: 1398,
                3: 1740,
                4: 1970,
              },
            },
            F: {
              21: {
                1: 417,
                2: 582,
                3: 726,
                4: 824,
              },
              22: {
                1: 457,
                2: 639,
                3: 796,
                4: 904,
              },
              23: {
                1: 500,
                2: 698,
                3: 870,
                4: 988,
              },
              24: {
                1: 544,
                2: 760,
                3: 948,
                4: 1076,
              },
              25: {
                1: 591,
                2: 825,
                3: 1028,
                4: 1167,
              },
              26: {
                1: 639,
                2: 892,
                3: 1112,
                4: 1263,
              },
              27: {
                1: 689,
                2: 962,
                3: 1200,
                4: 1362,
              },
              28: {
                1: 741,
                2: 1034,
                3: 1290,
                4: 1464,
              },
              29: {
                1: 795,
                2: 1110,
                3: 1384,
                4: 1571,
              },
              30: {
                1: 850,
                2: 1187,
                3: 1481,
                4: 1681,
              },
              31: {
                1: 908,
                2: 1268,
                3: 1581,
                4: 1795,
              },
            },
          },
          load_span: 1500,
          load_span_a: 965,
          load_span_b: 965,
        }
    },
    methods: {
      exportAsPdf() {
        axios.post('/technical/wind-load-calculator-pdf', {
          basic_wind_speed: this.basic_wind_speed,
          design_height: this.design_height,
          distance_from_sea: this.distance_from_sea,
          site_terrain_category_open_country_toggle: this.site_terrain_category_open_country_toggle,
          altitude: this.altitude,
          orography_factor: this.orography_factor,
          dormer_factor: this.dormer_factor,
          funnelling_factor: this.funnelling_factor,
          load_span: this.load_span,
          load_span_a: this.load_span_a,
          load_span_b: this.load_span_b,
          site_terrain_category: this.site_terrain_category,
          design_height_band: this.design_height_band,
          wind_load_at_sea_level: this.wind_load_at_sea_level,
          altitude_factor: this.altitude_factor,
          total_wind_load: this.total_wind_load,
          load_width_a: this.load_width_a,
          load_width_b: this.load_width_b,
          deflection: this.deflection,
          deflection_mm: this.deflection_mm,
          ei_required: this.ei_required,
        }, {
          responseType: 'blob',
          headers: {
            Accept: 'application/pdf',
          },
        }).then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'wind-load-calculator.pdf');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
      },
    },
    computed: {
      site_terrain_category() {
        if(this.site_terrain_category_open_country_toggle) {
          if(this.distance_from_sea < 1) {
            return 'A';
          } else if (this.distance_from_sea > 10) {
            return 'C';
          } else {
            return 'B';
          }
        } else {
          if(this.distance_from_sea < 1) {
            return 'D';
          } else if (this.distance_from_sea > 10) {
            return 'F';
          } else {
            return 'E';
          }
        }
      },
      design_height_band() {
        if (this.design_height >= 3 && this.design_height <= 6) {
          return 2;
        } else if (this.design_height > 6 && this.design_height <= 10) {
          return 3;
        } else if (this.design_height > 10) {
          return 4;
        } else {
          return 1;
        }
      },
      wind_load_at_sea_level() {
        return this.wind_load_mappings[this.site_terrain_category][this.basic_wind_speed][this.design_height_band];
      },
      altitude_factor() {
        return Math.pow((1 + (this.altitude / 1000)), 2);
      },
      total_wind_load() {
        let total = (this.wind_load_at_sea_level * this.altitude_factor * this.orography_factor * this.dormer_factor * this.funnelling_factor);

        if(total < 800) {
          return 800;
        }

        return total;
      },
      load_width_a() {
          let result = this.load_span_a / 2;
          let max = this.load_span / 2;

          if(result > max) {
              return max;
          }

          return result;
      },
      load_width_b() {
          let result = this.load_span_b / 2;
          let max = this.load_span / 2;

          if(result > max) {
              return max;
          }

          return result;
      },
      deflection() {
        if((this.load_span / 150) > 15) {
          return this.load_span / 15;
        } else {
          return 150;
        }
      },
      deflection_mm() {
        if((this.load_span / this.deflection) > 15) {
          return 15;
        } else {
          return this.load_span / this.deflection;
        }
      },
      ei_required() {
        return (((25 - (40 * Math.pow(this.load_width_a / this.load_span, 2)) + (16 * Math.pow(this.load_width_a / this.load_span, 4))) *
            (this.total_wind_load * this.load_width_a * Math.pow(this.load_span, 4))) /
          ((1.92 * Math.pow(10, 9)) * this.deflection_mm) + ((25 - (40 * Math.pow(this.load_width_b / this.load_span, 2)) + (16 * Math.pow(this.load_width_b / this.load_span, 4))) *
            (this.total_wind_load * this.load_width_b * Math.pow(this.load_span, 4))) /
          ((1.92 * Math.pow(10, 9)) * this.deflection_mm)) / 1000000000;
      },
    },
    watch: {
      design_height() {
        if(this.design_height > 15) {
          this.design_height = 15;
        }
      },
      altitude() {
        if(this.altitude > 425) {
          this.altitude = 425;
        }
      }
    }
};
</script>
